import { ElementType } from 'react';
import OrderStatusCheckIcon from '@/modules/order/components/OrderStatusCheckIcon';
import OrderStatusRejectedIcon from '@/modules/order/components/OrderStatusRejectedIcon';
import { OrderStatus as OrderStatusModel } from '@/modules/order/models/OrderStatus';

interface OrderStatusStepOrder {
  status: OrderStatusModel;
  editedBySupplier?: boolean;
}

interface Step {
  title: string;
  status: OrderStatusModel;
  description?: string;
  date?: string;
  by?: string;
  edited?: boolean;
  visible?: (order: OrderStatusStepOrder) => boolean;
  bgColor?: string;
  icon?: ElementType;
}

const isRejected = ({ status }: OrderStatusStepOrder) => status === OrderStatusModel.Rejected;
const isActiveOrder = (order: OrderStatusStepOrder) => !isRejected(order);
const hasSupplierEdits = ({ editedBySupplier }: OrderStatusStepOrder) => Boolean(editedBySupplier);
const isEditedBySupplier = (order: OrderStatusStepOrder) =>
  isActiveOrder(order) && hasSupplierEdits(order);
const isNotEditedBySupplier = (order: OrderStatusStepOrder) =>
  isActiveOrder(order) && !hasSupplierEdits(order);

const retailerSteps: Step[] = [
  { title: 'Order Created', status: OrderStatusModel.New },
  {
    title: 'Order Rejected',
    status: OrderStatusModel.Rejected,
    visible: isRejected,
    bgColor: 'bg-red-200',
    icon: OrderStatusRejectedIcon,
  },
  { title: 'Submitted to Supplier', status: OrderStatusModel.Submitted, visible: isActiveOrder },
  {
    title: 'Accepted by Supplier',
    status: OrderStatusModel.Accepted,
    visible: isNotEditedBySupplier,
  },
  {
    title: 'Edited & Accepted by Supplier',
    status: OrderStatusModel.Accepted,
    visible: isEditedBySupplier,
  },
  { title: 'Order Dispatched', status: OrderStatusModel.Dispatched, visible: isActiveOrder },
  { title: 'Received Order', status: OrderStatusModel.Received, visible: isActiveOrder },
  { title: 'Completed by Supplier', status: OrderStatusModel.Completed, visible: isActiveOrder },
];

const supplierSteps: Step[] = [
  { title: 'Order Received', status: OrderStatusModel.Submitted },
  {
    title: 'Order Rejected',
    status: OrderStatusModel.Rejected,
    visible: isRejected,
    bgColor: 'bg-red-200',
    icon: OrderStatusRejectedIcon,
  },
  { title: 'Order Accepted', status: OrderStatusModel.Accepted, visible: isActiveOrder },
  { title: 'Order Dispatched', status: OrderStatusModel.Dispatched, visible: isActiveOrder },
  { title: 'Order Received by Store', status: OrderStatusModel.Received, visible: isActiveOrder },
  { title: 'Order Completed', status: OrderStatusModel.Completed, visible: isActiveOrder },
];

interface OrderStatusProps {
  order?: OrderStatusStepOrder;
  isSupplier?: boolean;
}

const OrderStatus = ({ order, isSupplier = false }: OrderStatusProps) => {
  const status = order?.status;

  const steps = isSupplier ? supplierSteps : retailerSteps;

  let isCurrentOrPastStatus = false;

  let stepNumber = 0;

  return (
    <ol className="relative text-gray-500 border-s border-gray-400 dark:border-gray-700 dark:text-gray-400 ml-4">
      {steps.map((step, index) => {
        const isCurrentStatus = step.status === status;

        isCurrentOrPastStatus = isCurrentOrPastStatus || step.status === status;

        if (step.visible && status && !step.visible(order)) {
          return;
        }

        stepNumber++;

        const bgColor =
          !isCurrentOrPastStatus || isCurrentStatus ? step.bgColor || 'bg-green-200' : '';

        return (
          <li key={index} className="flex mb-6 ms-7">
            <span
              className={`absolute flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full -start-4 ring-4 ring-white dark:ring-gray-900 dark:bg-gray-700 ${bgColor}`}
            >
              {isCurrentStatus
                ? (step.icon && <step.icon />) || <OrderStatusCheckIcon />
                : stepNumber}
            </span>
            <div className="flex flex-col items-center">
              <h3 className="font-medium leading-tight py-1.5">{step.title}</h3>
              {step.description && <p className="text-sm">{step.description}</p>}
              {step.date && <p className="text-sm">{step.date}</p>}
              {step.by && <p className="text-sm">by {step.by}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default OrderStatus;
